// 승인 카드 렌더에 쓰이는 순수 헬퍼.
// pending_approval 의 action 을 도구 종류로 분류하고, args(blocks/plan)·description
// (task_reviewer 검토 의견 동봉) 을 안전하게 해석한다. 렌더 로직(JSX)은 approval.tsx.
import type { TaskEditorBlock } from "../utils/taskEditorUtils";
import type { BlockChange } from "./types";

const BLOCK_CHANGE_OPS = new Set([
  "replace_text",
  "replace_block",
  "insert_after",
  "insert_before",
  "delete",
  "move",
]);

// action_request.block_changes(ops 변경요약)를 안전하게 해석한다. 형태가 맞을 때만 캐스팅하고,
// 없거나 비었거나 어긋나면 null → 호출부가 전체 blocks preview 로 폴백한다. (args 가 아니라
// action 의 형제 필드 — diff 표시 전용이라 edit 결정의 args 왕복과 분리한다.)
export function asBlockChanges(raw: unknown): BlockChange[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const shapeOk = raw.every(
    (c) =>
      c != null &&
      typeof c === "object" &&
      BLOCK_CHANGE_OPS.has((c as { op?: unknown }).op as string)
  );
  return shapeOk ? (raw as BlockChange[]) : null;
}

// 백엔드 도구 표면: 활동지 변경은 update_task 단일 경로, 그 앞에 propose_plan 계획
// 게이트. 그 외(레거시 write_file mock, 미지 도구)는 generic fallback 으로 처리.
export type ActionKind = "plan" | "task" | "unknown";

export function getActionKind(name: string): ActionKind {
  if (name === "propose_plan") return "plan";
  if (name === "update_task") return "task";
  return "unknown";
}

// propose_plan 의 인자는 자유 형식 계획 텍스트 한 개.
export function asPlanText(args: Record<string, unknown>): string | null {
  const plan = (args as { plan?: unknown }).plan;
  return typeof plan === "string" ? plan : null;
}

// update_task 의 인자는 구조화된 blocks 배열. 런타임 unknown 이므로 형태를 확인한
// 뒤에만 캐스팅한다 — 아니면 null 을 돌려 호출부가 generic fallback 으로 빠지게 한다.
export function asBlocks(
  args: Record<string, unknown>
): TaskEditorBlock[] | null {
  const blocks = (args as { blocks?: unknown }).blocks;
  if (!Array.isArray(blocks)) return null;
  const shapeOk = blocks.every(
    (b) =>
      b != null &&
      typeof b === "object" &&
      Array.isArray((b as { description?: unknown }).description)
  );
  return shapeOk ? (blocks as TaskEditorBlock[]) : null;
}

// task_reviewer 검토 의견은 update_task 의 description 에 이 마커와 함께 동봉된다.
// 마커 이후를 리뷰 의견으로 분리해 카드에서 인용 박스로 보여준다. 마커가 없으면
// (단순 수정 등) 전체를 본문으로 둔다 — 파싱 실패가 빈 카드가 되지 않게.
const REVIEW_MARKER = "📝 task_reviewer 검토 의견";

export function splitReviewFeedback(description: string): {
  body: string;
  review: string | null;
} {
  const idx = description.indexOf(REVIEW_MARKER);
  if (idx === -1) return { body: description, review: null };
  const body = description.slice(0, idx).trimEnd();
  // 마커 라벨과 뒤따르는 ":"·공백·줄바꿈을 걷어내고 의견 본문만 남긴다.
  const review = description
    .slice(idx + REVIEW_MARKER.length)
    .replace(/^\s*:?\s*/, "")
    .trim();
  return { body, review: review.length > 0 ? review : null };
}

// diff 카드 내용 미리보기용 스니펫 — 공백 정리 + 길면 말줄임. 빈 문자열이면 null.
const SNIPPET_MAX = 80;
export function snippet(text: string | null | undefined): string | null {
  const t = (text ?? "").trim();
  if (!t) return null;
  return t.length > SNIPPET_MAX ? t.slice(0, SNIPPET_MAX) + "…" : t;
}

// 블록의 첫 text 컴포넌트 내용 (추가/교체 블록의 미리보기). text 가 없으면 null.
export function firstTextOfBlock(
  block: TaskEditorBlock | null | undefined
): string | null {
  if (!block || !Array.isArray(block.description)) return null;
  const text = block.description.find((c) => c.type === "text");
  return text && text.type === "text" ? snippet(text.content) : null;
}
